'use client'

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { ExternalLink, Loader2 } from 'lucide-react'

import type { IntegrationProvider } from '@/lib/integrations-constants'

// Form imports
import { PaymentMethodForm } from './forms/payment-method-form'
import { PixelTrackingForm } from './forms/pixel-tracking-form'
import { ShopifyForm } from './forms/shopify-form'
import { WoocommerceForm } from './forms/woocommerce-form'
import { PaypalForm } from './forms/paypal-form'
import { KlaviyoForm } from './forms/klaviyo-form'
import { BridgeForm } from './forms/bridge-form'
import { ConvergeForm } from './forms/converge-form'
import { AwinForm } from './forms/awin-form'
import { MetaConversionForm } from './forms/meta-conversion-form'
import { OceanPaymentForm } from './forms/oceanpayment-form'
import { GTMForm } from './forms/gtm-form'
import { ClarityForm } from './forms/clarity-form'
import { SnapchatForm } from './forms/snapchat-form'
import { TriplewhaleForm } from './forms/triplewhale-form'
import { SendCloudForm } from './forms/sendcloud-form'
import { KlarnaForm } from './forms/klarna-form'
import { WhopForm } from './forms/whop-form'
import { WhopPlatformsForm } from './forms/whop-platforms-form'
import { GooglePayForm } from './forms/google-pay-form'
import { ConvesioPayForm } from './forms/convesiopay-form'
import { HiPayForm } from './forms/hipay-form'
import { SlashForm } from './forms/slash-form'
import { TabbyForm } from './forms/tabby-form'
import { ZelleForm } from './forms/zelle-form'
import { CustomPaymentForm } from './forms/custom-payment-form'
import { ChargeblastForm } from './forms/chargeblast-form'
import { WebhooksForm } from './forms/webhooks-form'
import { WetrackedForm } from './forms/wetracked-form'
import { PostbackTrackerForm } from './forms/postback-tracker-form'
import { HyrosForm } from './forms/hyros-form'
import { AirwallexApmForm } from './forms/airwallex-apm-form'
import { CryptoForm } from './forms/crypto-form'
import { CryptoOnchainForm, type CryptoOnchainFormHandle } from './forms/crypto-onchain-form'
import { TaxjarForm } from './forms/taxjar-form'
import { TaxcloudForm } from './forms/taxcloud-form'
import { PlatformPayForm } from './forms/platformpay-form'
import { RxActivationCard } from '@/components/rx'
import { ApplePayForm } from './forms/apple-pay-form'
import { ApplePayDomainSection } from '@/app/stores/[id]/integrations/components/ApplePayDomainSection'
import { useEnablePlatformApplePay } from '@/lib/api/apple-pay-registrations'
import { useDisableCryptoOnchain } from '@/lib/api/crypto-onchain'

// ============================================================================
// Helpers
// ============================================================================

/**
 * Recursively trim every string in an integration config payload so a pixel ID,
 * API key, token, container ID, URL, etc. is never saved with leading/trailing
 * whitespace (a common copy-paste artifact that silently breaks tracking and
 * auth). Applied centrally at save time so it covers every provider form.
 */
function deepTrimStrings<T>(value: T): T {
  if (typeof value === 'string') return value.trim() as T
  if (Array.isArray(value)) return value.map((v) => deepTrimStrings(v)) as T
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) out[k] = deepTrimStrings(v)
    return out as T
  }
  return value
}

// ============================================================================
// Types
// ============================================================================

export interface Integration {
  id: string
  provider: IntegrationProvider
  name: string
  description?: string
  logoUrl?: string
  externalUrl?: string
  type: string
  enabled: boolean
  tags?: string[]
  credentials?: Record<string, any>
  settings?: Record<string, any>
  metadata?: Record<string, any>
  storeId: string
  accountId?: string
  orgSlug?: string
  createdAt?: Date | string | null
}

interface IntegrationConfigViewProps {
  integration: Integration
  onSave: (data: Record<string, any>) => Promise<void>
  onDisconnect?: () => Promise<void>
  onTestConnection?: () => Promise<void>
  isLoading?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function IntegrationConfigView({
  integration,
  onSave,
  onDisconnect,
  onTestConnection,
  isLoading = false,
}: IntegrationConfigViewProps) {
  const { t } = useTranslation('integrations')
  const [isTesting, setIsTesting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // A brand-new integration shows the switch ON before its first save. Crypto payments are only
  // "on" once the crypto-api merchant exists, so that provider always starts from the stored flag.
  const [isEnabled, setIsEnabled] = useState(
    integration.id || integration.provider === 'crypto_onchain' ? integration.enabled : true,
  )
  const enablePlatformApplePay = useEnablePlatformApplePay()
  const disableCryptoOnchain = useDisableCryptoOnchain()
  const cryptoOnchainFormRef = useRef<CryptoOnchainFormHandle>(null)

  if (integration.provider === 'tagada_rx') {
    return <RxActivationCard storeId={integration.storeId} />
  }

  // Integrations that are set up on the provider's own site have nothing to
  // store here: no credentials form, and no enable switch that would create an
  // integration row for a connection Tagada does not hold.
  if (integration.externalUrl) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('configView.externalTitle')}</CardTitle>
          <CardDescription>
            {t('configView.externalDescription', { name: integration.name })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={() => window.open(integration.externalUrl, '_blank')}>
            {t('configView.externalOpen', { name: integration.name })}
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    )
  }

  const handleTestConnection = async () => {
    if (!onTestConnection) return
    setIsTesting(true)
    try {
      await onTestConnection()
    } finally {
      setIsTesting(false)
    }
  }

  const handleSave = async (formData: Record<string, any>) => {
    setIsSaving(true)
    try {
      // Trim every string (api keys, ids, tokens, urls) before persisting, then
      // include the enabled state from the header switch.
      await onSave({ ...deepTrimStrings(formData), enabled: isEnabled })
    } finally {
      setIsSaving(false)
    }
  }

  // Toggle enabled and save immediately.
  //
  // TAG-2036: for `apple_pay`, this switch used to write `enabled: true`
  // straight to the DB with no check that Apple Pay actually works — a
  // merchant could flip it on and see "Connected" with no domain ever
  // registered with Apple. Turning it OFF stays a plain save (always safe);
  // turning it ON runs the real add/probe/register/activate pipeline instead,
  // auto-resolving the domain. If that fails (most commonly: no resolvable
  // domain), the switch reverts and the domain section below explains why —
  // that section's own button carries a manual domain override this one
  // doesn't need for the common case.
  const handleToggleEnabled = async (checked: boolean) => {
    if (integration.provider === 'apple_pay' && checked) {
      setIsSaving(true)
      try {
        const result = await enablePlatformApplePay.mutateAsync({ storeId: integration.storeId })
        if (result.steps.every((s) => s.ok)) setIsEnabled(true)
      } catch {
        // Toast already shown by the mutation hook; switch stays off.
      } finally {
        setIsSaving(false)
      }
      return
    }

    // Crypto payments (on-chain via crypto-api): ON provisions the crypto-api merchant with the payout
    // addresses the merchant typed. Those live in CryptoOnchainForm's own form state, which this view
    // cannot read, so the switch submits that form through its handle: the form validates (at least
    // one well-formed address), runs `cryptoOnchain.enable` and reports success through `onEnabled`,
    // which is what flips this switch. A refusal leaves it off, with the reason shown as a field
    // error or a toast. OFF calls `cryptoOnchain.disable` (link + integration row) instead of the
    // generic save, so `integrations.update` is never used for this provider.
    if (integration.provider === 'crypto_onchain') {
      setIsSaving(true)
      try {
        if (checked) {
          await cryptoOnchainFormRef.current?.submit()
        } else {
          await disableCryptoOnchain.mutateAsync({ storeId: integration.storeId })
          setIsEnabled(false)
        }
      } catch {
        // Toast already shown by the mutation hook; the switch keeps its current state.
      } finally {
        setIsSaving(false)
      }
      return
    }

    setIsEnabled(checked)
    setIsSaving(true)
    try {
      await onSave({
        credentials: integration.credentials || {},
        settings: integration.settings || {},
        metadata: integration.metadata || {},
        enabled: checked,
      })
    } finally {
      setIsSaving(false)
    }
  }

  // Render the appropriate form based on provider
  const renderForm = () => {
    const formKey = integration.id || 'new' // Force remount when integration changes to reset form with correct data
    const formProps = {
      defaultValues: {
        credentials: integration.credentials || {},
        settings: integration.settings || {},
        metadata: integration.metadata || {},
      },
      onSubmit: handleSave,
      isLoading: isSaving || isLoading,
    }

    switch (integration.provider) {
      // Storefront integrations
      case 'shopify':
        return <ShopifyForm key={formKey} {...formProps} storeId={integration.storeId} accountId={integration.accountId} orgSlug={integration.orgSlug} onDisconnect={onDisconnect} />
      case 'woocommerce':
        return <WoocommerceForm key={formKey} {...formProps} storeId={integration.storeId} accountId={integration.accountId} integrationCreatedAt={integration.createdAt} />

      // Payment integrations
      case 'apple_pay':
        return (
          <div key={formKey} className="space-y-6">
            <ApplePayDomainSection storeId={integration.storeId} onEnabled={() => setIsEnabled(true)} />
            <ApplePayForm {...formProps} />
          </div>
        )
      case 'paypal':
        return <PaypalForm key={formKey} {...formProps} />
      case 'bridge':
        return <BridgeForm key={formKey} {...formProps} />
      case 'oceanpayment':
        return <OceanPaymentForm key={formKey} {...formProps} />
      case 'google_pay':
        return <GooglePayForm key={formKey} {...formProps} />
      case 'klarna':
        return <KlarnaForm key={formKey} {...formProps} />
      case 'whop':
        return <WhopForm key={formKey} {...formProps} storeId={integration.storeId} />
      case 'whop_platforms':
        return <WhopPlatformsForm key={formKey} {...formProps} storeId={integration.storeId} />
      case 'convesiopay':
        return <ConvesioPayForm key={formKey} {...formProps} storeId={integration.storeId} accountId={integration.accountId} />
      case 'hipay':
        return <HiPayForm key={formKey} {...formProps} storeId={integration.storeId} />
      case 'slash':
        return <SlashForm key={formKey} {...formProps} />
      case 'tabby':
        return <TabbyForm key={formKey} {...formProps} storeId={integration.storeId} />
      case 'zelle':
        return <ZelleForm key={formKey} {...formProps} />
      case 'custom_payment':
        return (
          <CustomPaymentForm
            key={formKey}
            defaultValues={{
              ...formProps.defaultValues,
              displayName: integration.name,
              logoUrl: integration.logoUrl ?? '',
            }}
            onSubmit={handleSave}
            isLoading={isSaving || isLoading}
          />
        )

      // Pixel integrations
      case 'facebook':
      case 'tiktok':
        return <PixelTrackingForm key={formKey} {...formProps} provider={integration.provider} />
      case 'meta_conversion':
        return <MetaConversionForm key={formKey} {...formProps} />
      case 'awin_conversion':
        return <AwinForm key={formKey} {...formProps} />
      case 'gtm':
        return <GTMForm key={formKey} {...formProps} />
      case 'clarity':
        return <ClarityForm key={formKey} {...formProps} />
      case 'snapchat':
        return <SnapchatForm key={formKey} {...formProps} />
      case 'wetracked':
        return <WetrackedForm key={formKey} {...formProps} />

      // Postback ad trackers — same shared form, different provider id.
      // Each provider's metadata (display name, default placeholder,
      // docs URL) is looked up by the form via @/lib/postback-tracker-meta.
      case 'clickflare':
      case 'voluum':
      case 'binom':
      case 'redtrack':
      case 'clickmagick':
        return (
          <PostbackTrackerForm
            key={formKey}
            provider={integration.provider}
            {...formProps}
          />
        )

      // Hyros — PII-based attribution (email + phone), structurally
      // different from the postback family so it gets its own form.
      case 'hyros':
        return <HyrosForm key={formKey} {...formProps} />

      // Analytics integrations
      case 'klaviyo':
        return <KlaviyoForm key={formKey} {...formProps} />
      case 'converge':
        return <ConvergeForm key={formKey} {...formProps} />
      case 'triplewhale':
        return <TriplewhaleForm key={formKey} {...formProps} />
      case 'webhooks':
        return <WebhooksForm key={formKey} {...formProps} storeId={integration.storeId} />

      // Shipping
      case 'sendcloud':
        return <SendCloudForm key={formKey} {...formProps} />

      // Chargeback
      case 'chargeblast':
        return <ChargeblastForm key={formKey} {...formProps} />

      // Airwallex Alternative Payment Methods
      case 'airwallex_apm':
        return <AirwallexApmForm key={formKey} {...formProps} storeId={integration.storeId} />

      // Crypto (fiat-to-crypto via Rampex)
      case 'crypto':
        return <CryptoForm key={formKey} {...formProps} />

      // Crypto payments (on-chain via crypto-api): the form owns enable / update through the
      // cryptoOnchain mutations; `onSave` (integrations.update) is never used for this provider.
      case 'crypto_onchain':
        return (
          <CryptoOnchainForm
            key={formKey}
            ref={cryptoOnchainFormRef}
            storeId={integration.storeId}
            onEnabled={() => setIsEnabled(true)}
            isLoading={isSaving || isLoading}
          />
        )

      // Tax
      case 'taxjar':
        return <TaxjarForm key={formKey} {...formProps} />

      case 'taxcloud':
        return <TaxcloudForm key={formKey} {...formProps} />

      // Post-purchase reporting hooks
      case 'platformpay':
        return <PlatformPayForm key={formKey} {...formProps} />

      default:
        return null
    }
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              {integration.logoUrl && (
                <div className="flex h-14 w-14 items-center justify-center rounded-lg border bg-white p-2">
                  <img
                    src={integration.logoUrl}
                    alt={integration.name}
                    className="h-full w-full object-contain"
                  />
                </div>
              )}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-xl">{integration.name}</CardTitle>
                  {integration.tags && integration.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {integration.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                {integration.description && (
                  <CardDescription>{integration.description}</CardDescription>
                )}
              </div>
            </div>
            {/* Enable/Disable Switch */}
            <div className="flex items-center gap-3">
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <span className="text-sm text-muted-foreground">{isEnabled ? t('configView.enabled') : t('configView.disabled')}</span>
              )}
              <Switch
                checked={isEnabled}
                onCheckedChange={handleToggleEnabled}
                disabled={isSaving || isLoading}
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Configuration Form Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('configView.configuration')}</CardTitle>
          <CardDescription>
            {t('configView.configurationDescription', { name: integration.name })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {renderForm()}

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-3 border-t pt-6">
              {onTestConnection && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={isTesting || isSaving || isLoading}
                >
                  {isTesting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t('configView.testConnection')}
                </Button>
              )}
              <Button
                type="submit"
                form="integration-config-form"
                disabled={isSaving || isLoading}
              >
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('configView.saveConfiguration')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
